import React from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageSwitcherProps {
  variant?: 'sidebar' | 'standalone' | 'header';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'standalone' }) => {
  const { i18n } = useTranslation();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ro', name: 'Română', flag: '🇷🇴' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' }
  ];

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
  };

  const sidebarStyles = {
    container: {
      display: 'flex',
      gap: '4px',
      justifyContent: 'center'
    },
    button: (isActive: boolean) => ({
      background: isActive ? '#1976d2' : 'rgba(255,255,255,0.1)',
      color: 'white',
      border: `1px solid ${isActive ? '#1976d2' : 'rgba(255,255,255,0.2)'}`,
      borderRadius: '4px',
      padding: '4px 8px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      transition: 'all 0.2s ease',
      minWidth: '50px',
      justifyContent: 'center'
    })
  };

  const standaloneStyles = {
    container: {
      position: 'fixed' as const,
      bottom: 18,
      left: 18,
      zIndex: 1000,
      display: 'flex',
      gap: '8px',
      background: '#ffffff',
      borderRadius: '8px',
      padding: '8px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e0e0e0'
    },
    button: (isActive: boolean) => ({
      background: isActive ? '#2196f3' : 'transparent',
      color: isActive ? 'white' : '#333',
      border: 'none',
      borderRadius: '6px',
      padding: '6px 10px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.2s ease'
    })
  };

  const headerStyles = {
    container: {
      display: 'flex',
      gap: '6px'
    },
    button: (isActive: boolean) => ({
      background: isActive ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
      color: 'white',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: '6px',
      padding: '6px 10px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(10px)'
    })
  };

  const styles = variant === 'sidebar' ? sidebarStyles : 
                variant === 'header' ? headerStyles : standaloneStyles;

  return (
    <div style={styles.container}>
      {languages.map((language) => (
        <button
          key={language.code}
          onClick={() => changeLanguage(language.code)}
          style={styles.button(i18n.language === language.code)}
          onMouseEnter={(e) => {
            if (i18n.language !== language.code) {
              if (variant === 'standalone') {
                e.currentTarget.style.background = '#f5f5f5';
              } else if (variant === 'sidebar') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              } else if (variant === 'header') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }
            }
          }}
          onMouseLeave={(e) => {
            if (i18n.language !== language.code) {
              if (variant === 'standalone') {
                e.currentTarget.style.background = 'transparent';
              } else if (variant === 'sidebar') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              } else if (variant === 'header') {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              }
            }
          }}
          title={language.name}
        >
          <span style={{ fontSize: variant === 'sidebar' ? '14px' : '16px' }}>{language.flag}</span>
          {(variant === 'standalone' || variant === 'header') && <span>{language.code.toUpperCase()}</span>}
        </button>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
